import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import type { ProductListDescriptor } from '~/services/analytics/productLists';

const ProductListAnalyticsContext = createContext<ProductListDescriptor | null>(
  null,
);

export interface ProductListAnalyticsProviderProps {
  list: ProductListDescriptor;
  children: ReactNode;
}

/**
 * Impressions queue until the minute timer fires, so a list that unmounts
 * first — any navigation away — would lose its batch without the flush on
 * unmount.
 */
export const ProductListAnalyticsProvider = ({
  list,
  children,
}: ProductListAnalyticsProviderProps) => {
  const value = useMemo(
    () => ({
      id: list.id,
      name: list.name,
      source: list.source,
      category: list.category,
    }),
    [list.id, list.name, list.source, list.category],
  );

  useEffect(
    () => () => {
      analyticsService.flush(AnalyticsEvent.CATALOG_PRODUCT_SNIPPET_SHOWN);
    },
    [value],
  );

  return (
    <ProductListAnalyticsContext.Provider value={value}>
      {children}
    </ProductListAnalyticsContext.Provider>
  );
};

export const useProductListAnalyticsContext = () =>
  useContext(ProductListAnalyticsContext);
